/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';

export const qnaMakerTeachingBubbleMessages = defineMessages({
    createProjectTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.createProjectTitle',
        defaultMessage: 'Create a new project'
    },
    createProjectBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.createProjectBody',
        defaultMessage: 'Click create a new project to start a new custom question answering project and add data sources.'
    },
    sidebarCollapsedTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarCollapsedTitle',
        defaultMessage: 'We’ve collapsed the menu'
    },
    sidebarCollapsedBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarCollapsedBody',
        defaultMessage: 'We’ve collapsed the menu to give you more room. You can expand it again here'
    },
    createBotTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.createBotTitle',
        defaultMessage: 'Click to create a bot in Azure'
    },
    createBotBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.createBotBody',
        defaultMessage:
            'You will be redirected to the Azure portal to create a bot.\
             Click on create after filling all details to provision a bot resource.'
    },
    qnaEditTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.qnaEditTitle',
        defaultMessage: 'Edit question answer pair'
    },
    qnaEditBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.qnaEditBody',
        defaultMessage:
            'Make edits to questions, answers or metadata. Start by selecting a row with the question\
             answer pair you want to edit, then click on this edit button.'
    },
    synonymEditTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.synonymEditTitle',
        defaultMessage: 'Edit synonyms'
    },
    synonymEditBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.synonymEditBody',
        defaultMessage:
            'Make edits to synonyms. Start by selecting a row with the synonym\
            you want to edit, then click on this edit button.'
    },
    filterTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.filterTitle',
        defaultMessage: 'Filter question answers pairs'
    },
    filterBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.filterBody',
        defaultMessage: 'Filter question and answers by source and metadata. You can also filter unsaved changes.'
    },
    testTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.testTitle',
        defaultMessage: 'Test the responses'
    },
    testBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.testBody',
        defaultMessage:
            'After saving any changes you make, you can test how your model\
             is responding as if it were already connected to a chatbot'
    },
    showColumnsTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.showColumnsTitle',
        defaultMessage: 'Give yourself some more room'
    },
    showColumnsBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.showColumnsBody',
        defaultMessage: 'Focus on just the question and answer pairs by hiding the context and metadata columns'
    },
    saveChangesTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.saveChangesTitle',
        defaultMessage: 'Save the changes'
    },
    discardChangesTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.discardChangesTitle',
        defaultMessage: 'Discard the changes'
    },
    qnaSaveChangesBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.qnaSaveChangesBody',
        defaultMessage:
            'After editing question and answer pairs, you should save the changes.\
        You can use the filter to review unsaved changes.'
    },
    synonymSaveChangesBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.synonymSaveChangesBody',
        defaultMessage: 'After editing synonyms, you should save the changes.'
    },
    qnaDiscardChangesBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.qnaDiscardChangesBody',
        defaultMessage: 'After editing question and answer pairs, you can discard the changes'
    },
    synonymDiscardChangesBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.synonymDiscardChangesBody',
        defaultMessage: 'After editing synonyms, you can discard the changes.'
    },
    addQuestionPairTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.addQuestionPairTitle',
        defaultMessage: 'Add a new question'
    },
    addQuestionPairBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.addQuestionPairBody',
        defaultMessage: 'Add any question and answer pairs that weren’t in your sources'
    },
    qnaDeleteTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.qnaDeleteTitle',
        defaultMessage: 'Delete options'
    },
    qnaDeleteBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.qnaDeleteBody',
        defaultMessage: 'Delete the selected question and answer pair(s), prompts, or metadata'
    },
    synonymDeleteTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.synonymDeleteTitle',
        defaultMessage: 'Delete synonym group'
    },
    synonymDeleteBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.synonymDeleteBody',
        defaultMessage: 'Delete the selected synonym group'
    },
    activeReviewSuggestionTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.activeReviewSuggestionTitle',
        defaultMessage: 'Accept or reject suggestion'
    },
    activeReviewSuggestionBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.activeReviewSuggestionBody',
        defaultMessage:
            'Add the suggestion as an alternate question by accepting the suggestion.\
        You can also accept or reject all suggestions from the command bar.'
    },
    addSourceTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.addSourceTitle',
        defaultMessage: 'Add data sources to your project'
    },
    addSourceBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.addSourceBody',
        defaultMessage:
            'You can add FAQs, manuals, and other structured sources to extract question\
             and answer pairs or add unstructured content to enable search over text.\
             You can also add a personality to your knowledge base by selecting a chit-chat data set.'
    },
    firstSourceCreatedTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.firstSourceCreatedTitle',
        defaultMessage: 'Look at questions from one source'
    },
    firstSourceCreatedBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.firstSourceCreatedBody',
        defaultMessage: 'You can also click on a source to view and edit the questions from just that source'
    },
    sidebarDeployTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarDeployTitle',
        defaultMessage: 'Deploy and use'
    },
    sidebarDeployBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarDeployBody',
        defaultMessage:
            'Once your knowledge base is ready, you can deploy it. Once deployed,\
             you can go to the Azure portal to integrate it with a bot.'
    },
    sidebarEditTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarEditTitle',
        defaultMessage: 'Edit your knowledge base'
    },
    sidebarEditBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarEditBody',
        defaultMessage:
            'After adding your sources, edit your knowledge base to view the extracted question\
             and answer pairs, add alternative phrasing, or add conversational flow'
    },
    sidebarProjectSettingsTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarProjectSettingsTitle',
        defaultMessage: 'Set project level information'
    },
    sidebarProjectSettingsBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarProjectSettingsBody',
        defaultMessage: 'Edit the project name and description, and select the default answer.'
    },
    sidebarReviewSuggestionTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarReviewSuggestionTitle',
        defaultMessage: 'Review active learning suggestions'
    },
    sidebarReviewSuggestionBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.sidebarReviewSuggestionBody',
        defaultMessage: 'Review and add suggestions for questions based on how users are interacting with your bot.'
    },
    addSynonymTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.addSynonymTitle',
        defaultMessage: 'Add Synonyms'
    },
    addSynonymBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.addSynonymBody',
        defaultMessage: 'Add synonyms for terms that might be used in queries of your knowledge base.'
    },
    azureSearchTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.azureSearchTitle',
        defaultMessage: 'Connect to Azure Search'
    },
    azureSearchBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.azureSearchBody',
        defaultMessage:
            'To start creating a custom question answering project, you first need to create and connect Azure search to your Language resource'
    },
    selectAzureSearchTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.selectAzureSearchTitle',
        defaultMessage: 'Create new or connect to Azure Search resource.'
    },
    selectAzureSearchBody: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.selectAzureSearchBody',
        defaultMessage:
            'Please select the Azure Search instance that you would like to associate with your custom question answering project'
    },
    alternateQuestionTitle: {
        id: 'qnaMaker.qnaMakerTeachingBubbleMessages.alternateQuestionTitle',
        defaultMessage: 'You can drag the alternate questions from here and sort them anyway you like!'
    }
});
